import Phaser from "phaser";
import PlayerService from '../services/PlayerService.js';

const CONFIG = {
  maxSpeed: 350,
  // Configuration pour spritesheet "Marin.png" (2688x1920)
  // Grille : 56 colonnes x 20 lignes (Tiles 48x96)
  // Ligne 2 visuelle (Index 56 à 111) = Idle
  // Ligne 3 visuelle (Index 112 à 167) = Marche (Walk)
  animations: [
    // Idle (Ligne 2)
    { key: "idle-right", start: 56, end: 61, frameRate: 5 },
    { key: "idle-up", start: 62, end: 67, frameRate: 5 },
    { key: "idle-left", start: 68, end: 73, frameRate: 5 },
    { key: "idle-down", start: 74, end: 79, frameRate: 5 },
    // Walk (Ligne 3)
    { key: "walk-right", start: 112, end: 117, frameRate: 10 },
    { key: "walk-up", start: 118, end: 123, frameRate: 10 },
    { key: "walk-left", start: 124, end: 129, frameRate: 10 },
    { key: "walk-down", start: 130, end: 135, frameRate: 10 },
  ],
};

export class PlayerManager {
  constructor(scene) {
    this.scene = scene;
    this.player = null;
    this.isSpeedBoosted = false;
    this.currentAnim = "";
    this.lastDirection = "down"; // Default direction
    this.lastMoveSent = 0;
    this.playerPosition = null;
  }

  async createPlayer(playerData, textureKey, position) {
    if (!playerData) {
      console.error("Player data is not defined!");
      return;
    }

    const useTextureKey = textureKey || "playerAppearance";
    const { x, y } = position || this.playerPosition || { x: 0, y: 0 };
    
    this.player = this.scene.physics.add.sprite(x, y, useTextureKey);
    this.player.setDepth(1); // Ensure player is above floor/walls (depth 0)
    this.player.setCollideWorldBounds(true);
    this.player.body.setMaxVelocity(CONFIG.maxSpeed, CONFIG.maxSpeed);
    // this.player.setImmovable(true); // REMOVED: Player must be movable to collide with static objects
    this.player.body.setSize(36, 36); // Hitbox plus petite pour les pieds
    this.player.body.setOffset(8, 60); // Décalage vers le bas (96 - 32 - 4) pour coller aux pieds

    this.createAnimations(useTextureKey);
    return this.player;
  }

  createAnimations(textureKey) {
    const texExists = this.scene?.textures?.exists?.(textureKey);
    if (!texExists) {
      console.warn(`[PlayerManager] Texture missing for animations: ${textureKey}`);
      return;
    }

    const tex = this.scene.textures.get(textureKey);
    const totalFrames = typeof tex?.frameTotal === 'number' ? tex.frameTotal : null;

    CONFIG.animations.forEach(anim => {
      const animationKey = anim.key;

      if (this.scene.anims.exists(animationKey)) {
        console.warn(`AnimationManager key already exists: ${animationKey}`);
        return;
      }

      if (typeof totalFrames === 'number' && totalFrames > 0 && anim.end >= totalFrames) {
        console.warn(
          `[PlayerManager] Skip animation '${animationKey}' (frames ${anim.start}-${anim.end}) because '${textureKey}' has only ${totalFrames} frames.`
        );
        return;
      }

      let frames;
      try {
        frames = this.scene.anims.generateFrameNumbers(textureKey, { start: anim.start, end: anim.end });
      } catch (e) {
        console.warn(`[PlayerManager] Failed to generate frames for '${animationKey}' from '${textureKey}'`, e);
        return;
      }

      if (!frames || frames.length === 0) {
        console.warn(`[PlayerManager] No frames generated for '${animationKey}' from '${textureKey}'`);
        return;
      }

      this.scene.anims.create({
        key: animationKey,
        frames,
        frameRate: 8,
        repeat: -1,
      });
    });

    if (!this.scene.anims.exists('chest-open')) {
      if (!this.scene.textures.exists('!Chest')) {
        console.warn("[PlayerManager] Texture '!Chest' missing; skip chest-open animation");
        return;
      }
      this.scene.anims.create({
        key: 'chest-open',
        frames: this.scene.anims.generateFrameNumbers('!Chest', { start: 0, end: 3 }),
        frameRate: 10,
        repeat: 0
      });
    }
  }

  handleMovement(cursors, joystick, socket, myId) {
    if (!this.player || !this.player.body || !cursors) return;

    // Block movement if dialogue is active
    if (this.scene.uiManager && this.scene.uiManager.isDialogueActive) {
        this.player.setVelocity(0);
        this.player.anims.stop();
        return;
    }

    // const gridSize = 8; // unused — removed to avoid lint warning
    let newAnim = "";
    let keyboardActive = false;
    const speed = this.isSpeedBoosted ? 300 : 180;

    // Gestion clavier
    if (cursors.left?.isDown) {
      keyboardActive = true;
      newAnim = "walk-left";
      this.lastDirection = "left";
      this.player.setVelocity(-speed, 0);
    } else if (cursors.right?.isDown) {
      keyboardActive = true;
      newAnim = "walk-right";
      this.lastDirection = "right";
      this.player.setVelocity(speed, 0);
    } else if (cursors.up?.isDown) {
      keyboardActive = true;
      newAnim = "walk-up";
      this.lastDirection = "up";
      this.player.setVelocity(0, -speed);
    } else if (cursors.down?.isDown) {
      keyboardActive = true;
      newAnim = "walk-down";
      this.lastDirection = "down";
      this.player.setVelocity(0, speed);
    }

    // Gestion joystick
    // IMPORTANT: on some overlays (dialogue/combat/scene pause), the joystick can miss pointerup
    // and keep a stale non-zero force. Only trust the joystick when its pointer is currently down.
    const joystickPointerDown = (() => {
      try {
        const p = joystick?.pointer;
        if (p && typeof p.isDown === 'boolean') return p.isDown;
      } catch (e) {}
      try {
        if (joystick && typeof joystick.isDown === 'boolean') return joystick.isDown;
      } catch (e) {}
      return false;
    })();

    if (!keyboardActive && joystick && joystickPointerDown && joystick.force > 0) {
      const angle = joystick.angle;
      if (angle > -45 && angle <= 45) {
        newAnim = "walk-right";
        this.lastDirection = "right";
      } else if (angle > 45 && angle <= 135) {
        newAnim = "walk-down";
        this.lastDirection = "down";
      } else if (angle > 135 || angle <= -135) {
        newAnim = "walk-left";
        this.lastDirection = "left";
      } else if (angle > -135 && angle <= -45) {
        newAnim = "walk-up";
        this.lastDirection = "up";
      }

      // Le plugin renvoie généralement une force normalisée (0..1). On remap pour que
      // la vitesse max soit atteinte avec un très petit glissement du pouce.
      const deadzone = 0.06;
      const maxSpeedAtForce = 0.22; // ~22% de la course pour atteindre 100% vitesse
      const rawForce = typeof joystick.force === 'number' ? joystick.force : 0;

      if (rawForce <= deadzone) {
        this.player.setVelocity(0);
      } else {
        const normalized = Phaser.Math.Clamp((rawForce - deadzone) / Math.max(0.001, (maxSpeedAtForce - deadzone)), 0, 1);
        // Légère courbe pour rendre la montée plus rapide au début sans perdre le contrôle.
        const eased = Math.pow(normalized, 0.6);
        const rad = Phaser.Math.DegToRad(angle);
        this.player.setVelocityX(Math.cos(rad) * speed * eased);
        this.player.setVelocityY(Math.sin(rad) * speed * eased);
      }
    }

    // Animation
    if (newAnim === "") {
      this.player.setVelocity(0);
      const idleAnim = `idle-${this.lastDirection}`;
      if (this.currentAnim !== idleAnim) {
        this.player.anims.play(idleAnim, true);
        this.currentAnim = idleAnim;
      }
      this.currentAnim = "";
    } else if (newAnim !== this.currentAnim) {
      this.player.anims.play(newAnim, true);
      this.currentAnim = newAnim;
    }

    // Envoi position socket
    if (socket && myId) {
      const currentMapId = PlayerService.getPlayerData()?.mapId;
      socket.emit('playerMove', {
        x: this.player.x,
        y: this.player.y,
        anim: this.currentAnim,
        mapId: currentMapId,
      });
    }
  }

  sendMovementUpdate(socket, myId) {
    const now = Date.now();
    if (socket && myId && now - this.lastMoveSent > 50) {
      const currentMapId = PlayerService.getPlayerData()?.mapId;
      socket.emit('playerMove', {
        x: this.player.x,
        y: this.player.y,
        anim: this.currentAnim,
        mapId: currentMapId,
      });
      this.lastMoveSent = now;
    }
  }

  async updatePositionInDB() {
    if (!this.player || !this.scene.registry.get("playerData")) {
      console.warn("Player or player data is not initialized. Skipping position update.");
      return;
    }

    const playerData = this.scene.registry.get("playerData");
    const playerPseudo = playerData.pseudo;
    const posX = this.player.x;
    const posY = this.player.y;
    const mapId = playerData.mapId;

    console.log(`[PlayerManager] Saving position to DB: x=${Math.round(posX)}, y=${Math.round(posY)}, mapId=${mapId}`);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/players/update-position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: playerPseudo, posX, posY, mapId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update player position. HTTP status: ${response.status}, Response: ${errorText}`);
      }
    } catch (error) {
      console.error("Error updating player position:", error);
    }
  }

  setSpeedBoost(boosted) {
    this.isSpeedBoosted = boosted;
  }

  setPlayerPosition(position) {
    this.playerPosition = position;
  }

  getPlayer() {
    return this.player;
  }

  getCurrentAnim() {
    return this.currentAnim;
  }

  destroy() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }
}